import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { products, categories, siteImages } from '../data/mock';
import { useCart } from '../context/CartContext';
import { ChevronDown, Filter, X, Check, ArrowRight, ArrowUpRight } from 'lucide-react';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState('featured');
  const [showFilters, setShowFilters] = useState(false);
  const { addToCart } = useCart();

  const selectedCategory = searchParams.get('category') || 'all';
  const activeCategoryObj = categories.find((c) => c.slug === selectedCategory);

  useEffect(() => {
    document.title = 'Glasses, Lenses & Vision Solutions | Clarity Lens Co';
  }, []);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (selectedCategory !== 'all') {
      result = result.filter((p) => p.categorySlug === selectedCategory);
    }
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }
    return result;
  }, [selectedCategory, sortBy]);

  const handleCategoryChange = (slug) => {
    if (slug === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', slug);
    }
    setSearchParams(searchParams);
    setShowFilters(false);
  };

  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      quantity: 1,
    });
  };

  const bannerTitle = activeCategoryObj ? activeCategoryObj.name : 'The Collection';
  const bannerSubtitle = activeCategoryObj ? activeCategoryObj.description : 'Precision-crafted eyewear for every way of seeing.';
  const bannerImage = activeCategoryObj ? activeCategoryObj.image : siteImages.shopBanner;

  return (
    <div className="min-h-screen bg-cream-50">
      <Header />
      <CartDrawer />

      <main className="pt-[116px] lg:pt-[136px]">
        {/* Editorial Banner */}
        <section className="relative">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10 lg:pt-16 pb-8 lg:pb-14">
            <div className="grid lg:grid-cols-12 gap-8 items-end">
              <div className="lg:col-span-7">
                <nav className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-ink-400 font-mono mb-6">
                  <Link to="/" className="hover:text-ink-900">Home</Link>
                  <span>/</span>
                  <Link to="/shop" className="hover:text-ink-900">Shop</Link>
                  {activeCategoryObj && (
                    <>
                      <span>/</span>
                      <span className="text-ink-900">{activeCategoryObj.name}</span>
                    </>
                  )}
                </nav>
                <span className="text-eyebrow text-copper-600 font-mono">— All Eyewear</span>
                <h1 className="font-display text-ink-900 text-display-2xl mt-4">
                  {bannerTitle.includes('Glasses') || bannerTitle.includes('Eyewear') ? (
                    <>{bannerTitle.split(' ').slice(0, -1).join(' ')} <span className="italic font-light">{bannerTitle.split(' ').slice(-1)[0]}</span>.</>
                  ) : (
                    <><span className="italic font-light">{bannerTitle}</span>.</>
                  )}
                </h1>
                <p className="mt-5 text-ink-500 text-lg max-w-xl">{bannerSubtitle}</p>
              </div>
              <div className="lg:col-span-5">
                <div className="aspect-[16/9] lg:aspect-[5/6] relative overflow-hidden bg-cream-100">
                  <img src={bannerImage} alt={bannerTitle} className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Toolbar */}
        <div className="border-y border-ink-100 bg-cream-50 sticky top-[116px] lg:top-[136px] z-30">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="flex items-center justify-between h-14">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowFilters(true)}
                  className="flex items-center gap-2 text-sm tracking-[0.15em] uppercase font-mono text-ink-900 hover:text-copper-600 transition-colors"
                >
                  <Filter className="w-4 h-4" />
                  Filter
                </button>
                <span className="text-ink-300">|</span>
                <span className="text-[11px] tracking-[0.2em] uppercase text-ink-500 font-mono">
                  {filteredProducts.length} Products
                </span>
              </div>
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none bg-transparent border-0 pr-6 text-sm tracking-[0.15em] uppercase font-mono text-ink-900 cursor-pointer focus:outline-none"
                >
                  <option value="featured">Sort: Featured</option>
                  <option value="price-low">Price: Low → High</option>
                  <option value="price-high">Price: High → Low</option>
                  <option value="name">Name: A → Z</option>
                </select>
                <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Main */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-20">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            {/* Sidebar */}
            <aside className="hidden lg:block w-56 flex-shrink-0">
              <div className="sticky top-[200px] space-y-10">
                <div>
                  <h3 className="text-eyebrow text-copper-600 font-mono mb-6">Categories</h3>
                  <div className="space-y-1">
                    <button
                      onClick={() => handleCategoryChange('all')}
                      className={`w-full text-left py-2 text-sm transition-colors ${
                        selectedCategory === 'all'
                          ? 'text-ink-900 font-medium'
                          : 'text-ink-500 hover:text-ink-900'
                      }`}
                    >
                      All Products
                      {selectedCategory === 'all' && <span className="ml-2 text-copper-600">•</span>}
                    </button>
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleCategoryChange(cat.slug)}
                        className={`block w-full text-left py-2 text-sm transition-colors ${
                          selectedCategory === cat.slug
                            ? 'text-ink-900 font-medium'
                            : 'text-ink-500 hover:text-ink-900'
                        }`}
                      >
                        {cat.name}
                        {selectedCategory === cat.slug && <span className="ml-2 text-copper-600">•</span>}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="border-t border-ink-200 pt-8">
                  <h3 className="text-eyebrow text-copper-600 font-mono mb-4">Quality</h3>
                  <p className="text-sm text-ink-500 leading-relaxed">
                    Every pair includes UV400 protection, anti-scratch coatings and premium materials.
                  </p>
                </div>
              </div>
            </aside>

            {/* Product Grid - Tall Editorial Cards */}
            <div className="flex-1">
              {filteredProducts.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-16">
                  {filteredProducts.map((product, idx) => (
                    <Link key={product.id} to={`/product/${product.slug}`} className="group">
                      <div className="relative aspect-[4/5] bg-cream-100 overflow-hidden mb-5">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-contain p-6 group-hover:scale-[1.05] transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
                        />
                        {product.badge && (
                          <span className="absolute top-4 left-4 px-3 py-1 bg-cream-50 text-ink-900 text-[10px] tracking-[0.2em] uppercase font-mono">
                            {product.badge}
                          </span>
                        )}
                        <span className="absolute top-4 right-4 text-[10px] tracking-[0.22em] uppercase font-mono text-ink-500">
                          {String(idx + 1).padStart(2, '0')}
                        </span>
                        {/* Quick View on hover */}
                        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-0" />
                        <button
                          onClick={(e) => handleAddToCart(e, product)}
                          className="absolute bottom-0 left-0 right-0 py-4 bg-ink-900 text-cream-50 text-sm tracking-[0.15em] uppercase font-mono translate-y-full group-hover:translate-y-0 transition-transform duration-500 hover:bg-copper-600"
                        >
                          Add to Bag
                        </button>
                      </div>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-[10px] tracking-[0.22em] uppercase text-ink-400 font-mono mb-1">
                            {categories.find((c) => c.id === product.categoryId)?.name}
                          </p>
                          <h3 className="font-display text-lg text-ink-900 leading-tight group-hover:italic group-hover:text-copper-600 transition-all">
                            {product.name}
                          </h3>
                        </div>
                        <p className="font-display text-lg text-ink-900 whitespace-nowrap">
                          ${product.price.toFixed(2)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-32 border border-dashed border-ink-200">
                  <h3 className="font-display text-2xl text-ink-900 mb-3">No products in this view.</h3>
                  <p className="text-ink-500 mb-8">Explore the full collection below.</p>
                  <button
                    onClick={() => handleCategoryChange('all')}
                    className="inline-flex items-center gap-2 px-8 py-3 bg-ink-900 text-cream-50 hover:bg-copper-600 transition-colors"
                  >
                    <span className="text-sm tracking-[0.15em] uppercase font-mono">View all eyewear</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile filter drawer */}
        {showFilters && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-ink-900/50" onClick={() => setShowFilters(false)} />
            <div className="absolute bottom-0 left-0 right-0 bg-cream-50 max-h-[80vh] overflow-y-auto">
              <div className="sticky top-0 bg-cream-50 border-b border-ink-100 px-5 py-4 flex items-center justify-between">
                <h3 className="font-display text-xl text-ink-900">Categories</h3>
                <button onClick={() => setShowFilters(false)} className="p-2">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-1">
                <button
                  onClick={() => handleCategoryChange('all')}
                  className={`w-full flex items-center justify-between px-3 py-3 text-sm transition-colors ${
                    selectedCategory === 'all'
                      ? 'bg-ink-900 text-cream-50'
                      : 'text-ink-600 hover:bg-cream-100'
                  }`}
                >
                  <span>All Products</span>
                  {selectedCategory === 'all' && <Check className="w-4 h-4" />}
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`w-full flex items-center justify-between px-3 py-3 text-sm transition-colors ${
                      selectedCategory === cat.slug
                        ? 'bg-ink-900 text-cream-50'
                        : 'text-ink-600 hover:bg-cream-100'
                    }`}
                  >
                    <span>{cat.name}</span>
                    {selectedCategory === cat.slug && <Check className="w-4 h-4" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default ShopPage;
